// src/pages/trading-portal/TradeExecution/ConnectAccountTab.js
// Reusable "Connect Account" tab: list, add/edit/delete, and test broker connections.
// Shared between Order Management and AI Trading Agents pages.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    IconButton,
    Tooltip,
    Grid,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import {
    BrokerName, BrokerNameLabels, BrokerNameColors,
    ConnectionStatusLabels, ConnectionStatusColors,
    ProtocolType, ProtocolTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '-');

const ConnectAccountTab = ({ canAdd = true, canEdit = true, canDelete = true, onConnectionsChange }) => {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [testingConnectionId, setTestingConnectionId] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        broker_name: '',
        connection_name: '',
        protocol_type: '',
        api_endpoint: '',
        is_paper_trading: false,
        credentials_api_key: '',
        credentials_api_secret: '',
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchConnections = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/trade-execution/broker-connections');
            if (res.data.success) {
                setConnections(res.data.data || []);
            } else {
                setError(res.data.message || 'Failed to fetch broker connections');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch broker connections');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    const handleOpenDialog = (row = null) => {
        if (row) {
            setEditing(row);
            setForm({
                broker_name: row.broker_name || '',
                connection_name: row.connection_name || '',
                protocol_type: row.protocol_type || '',
                api_endpoint: row.api_endpoint || '',
                is_paper_trading: row.is_paper_trading || false,
                credentials_api_key: '',
                credentials_api_secret: '',
            });
        } else {
            setEditing(null);
            setForm({
                broker_name: '',
                connection_name: '',
                protocol_type: '',
                api_endpoint: '',
                is_paper_trading: false,
                credentials_api_key: '',
                credentials_api_secret: '',
            });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                broker_name: form.broker_name,
                connection_name: form.connection_name,
                protocol_type: form.protocol_type,
                api_endpoint: form.api_endpoint,
                is_paper_trading: form.is_paper_trading,
            };
            if (form.credentials_api_key || form.credentials_api_secret) {
                payload.credentials_encrypted = {
                    api_key: form.credentials_api_key,
                    api_secret: form.credentials_api_secret,
                };
            }
            let res;
            if (editing) {
                res = await axiosInstance.put(`/api/trade-execution/broker-connections/${editing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/trade-execution/broker-connections', payload);
            }
            if (res.data.success) {
                setDialogOpen(false);
                setSnackbar({ open: true, message: `Account ${editing ? 'updated' : 'connected'} successfully`, severity: 'success' });
                fetchConnections();
                if (onConnectionsChange) onConnectionsChange();
            } else {
                setError(res.data.message || 'Failed to save account');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save account');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/trade-execution/broker-connections/${deleteTarget.id}`);
            if (res.data.success) {
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Account disconnected', severity: 'success' });
                fetchConnections();
                if (onConnectionsChange) onConnectionsChange();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to disconnect account', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to disconnect account', severity: 'error' });
        }
    };

    const handleTestConnection = async (connectionId) => {
        setTestingConnectionId(connectionId);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/broker-connections/${connectionId}/test`);
            if (res.data.success) {
                const accountInfo = res.data.data?.account_info;
                const msg = accountInfo
                    ? `Connected! Buying Power: $${parseFloat(accountInfo.buying_power).toLocaleString()}, Equity: $${parseFloat(accountInfo.equity).toLocaleString()}`
                    : 'Connection test successful';
                setSnackbar({ open: true, message: msg, severity: 'success' });
                fetchConnections();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Connection test failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Connection test failed', severity: 'error' });
        } finally {
            setTestingConnectionId(null);
        }
    };

    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    return (
        <Box>
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Connect Account
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchConnections}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleOpenDialog()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Connect Account
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {loading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Broker Name</TableCell>
                                    <TableCell sx={headCellSx}>Connection Name</TableCell>
                                    <TableCell sx={headCellSx}>Protocol</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={headCellSx}>Paper Trading</TableCell>
                                    <TableCell sx={headCellSx}>Last Connected</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {connections.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.Cable sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No accounts connected</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : connections.map((conn) => (
                                    <TableRow key={conn.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell>
                                            <Chip
                                                label={BrokerNameLabels[conn.broker_name] || conn.broker_name}
                                                size="small"
                                                color={BrokerNameColors[conn.broker_name] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {conn.connection_name || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                            {ProtocolTypeLabels[conn.protocol_type] || conn.protocol_type || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ConnectionStatusLabels[conn.connection_status] || conn.connection_status || 'Unknown'}
                                                size="small"
                                                color={ConnectionStatusColors[conn.connection_status] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                            {conn.is_paper_trading ? (
                                                <Chip label="Paper" size="small" color="warning" variant="outlined" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                                            ) : (
                                                <Chip label="Live" size="small" color="success" variant="outlined" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            {formatDateTime(conn.last_connected_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenDialog(conn)}>
                                                            <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Disconnect">
                                                        <IconButton size="small" onClick={() => { setDeleteTarget(conn); setDeleteDialogOpen(true); }}>
                                                            <MuiIcons.Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Test Connection">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleTestConnection(conn.id)}
                                                        disabled={testingConnectionId === conn.id}
                                                    >
                                                        {testingConnectionId === conn.id ? (
                                                            <MuiIcons.HourglassEmpty sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                        ) : (
                                                            <MuiIcons.NetworkCheck sx={{ fontSize: 18, color: ACCENT }} />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                    {editing ? 'Edit Account' : 'Connect Account'}
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily }}>Broker Name</InputLabel>
                                <Select
                                    value={form.broker_name}
                                    onChange={(e) => {
                                        const newBroker = e.target.value;
                                        setForm((prev) => {
                                            const updates = { ...prev, broker_name: newBroker };
                                            if (newBroker === 'ALPACA' && !editing) {
                                                updates.protocol_type = 'REST_API';
                                                updates.api_endpoint = 'https://paper-api.alpaca.markets/v2';
                                                updates.is_paper_trading = true;
                                                updates.connection_name = prev.connection_name || 'Alpaca Paper Trading';
                                            }
                                            return updates;
                                        });
                                    }}
                                    label="Broker Name"
                                    sx={{ fontFamily }}
                                >
                                    {Object.entries(BrokerName).map(([key, val]) => (
                                        <MenuItem key={key} value={val} sx={{ fontFamily }}>{BrokerNameLabels[val]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Connection Name"
                                value={form.connection_name}
                                onChange={(e) => setForm((prev) => ({ ...prev, connection_name: e.target.value }))}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily }}>Protocol Type</InputLabel>
                                <Select
                                    value={form.protocol_type}
                                    onChange={(e) => setForm((prev) => ({ ...prev, protocol_type: e.target.value }))}
                                    label="Protocol Type"
                                    sx={{ fontFamily }}
                                >
                                    {Object.entries(ProtocolType).map(([key, val]) => (
                                        <MenuItem key={key} value={val} sx={{ fontFamily }}>{ProtocolTypeLabels[val]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="API Endpoint"
                                value={form.api_endpoint}
                                onChange={(e) => setForm((prev) => ({ ...prev, api_endpoint: e.target.value }))}
                                placeholder="https://api.broker.com/v1"
                                sx={{ fontFamily, '& input': { fontFamily } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_paper_trading}
                                        onChange={(e) => setForm((prev) => ({ ...prev, is_paper_trading: e.target.checked }))}
                                        sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                                    />
                                }
                                label={<Typography sx={{ fontFamily, fontSize: '0.875rem' }}>Paper Trading (Simulated)</Typography>}
                            />
                        </Grid>
                        {form.broker_name === 'ALPACA' && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Alpaca API Key"
                                        value={form.credentials_api_key}
                                        onChange={(e) => setForm((prev) => ({ ...prev, credentials_api_key: e.target.value }))}
                                        placeholder="PK..."
                                        sx={{ fontFamily, '& input': { fontFamily } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Alpaca API Secret"
                                        type="password"
                                        value={form.credentials_api_secret}
                                        onChange={(e) => setForm((prev) => ({ ...prev, credentials_api_secret: e.target.value }))}
                                        sx={{ fontFamily, '& input': { fontFamily } }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={submitting || !form.broker_name || !form.connection_name || !form.protocol_type}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {submitting ? 'Saving...' : (editing ? 'Update' : 'Connect')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                    Disconnect Account
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                        Are you sure you want to disconnect <strong>{deleteTarget?.connection_name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} variant="contained" color="error" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Disconnect
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ConnectAccountTab;
